import { Component, ChangeDetectorRef, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { UserSettingsStorage } from './user-settings-storage';
import { Observable, Subscription, combineLatest } from 'rxjs';
import { User } from './user';
import { map, filter } from 'rxjs/operators';
import { CultureInfo } from '../core/culture-info';
import { UnitOption } from '../lab-test-results';
import { LanguagesStore } from '../core';
import { ActivityLogger } from '../activities/activity-logger';
import { routeData } from '../shared/route-helpers';
import { UnitStorage } from './unit-storage';

@Component({
    selector: 'user-general-preferences',
    template: require('./user-general-preferences-component.html'),
    styles: [require('./user-general-preferences-component.scss')],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class UserGeneralPreferencesComponent implements OnDestroy {
    public readonly languages: Observable<CultureInfo[]>;
    public readonly unitOptions: UnitOption[];
    public preferredLanguage: CultureInfo;
    public preferredUnit: UnitOption;

    private readonly editedUser$: Observable<User>;
    private readonly subscription: Subscription;

    constructor(
        private readonly userSettingsStorage: UserSettingsStorage,
        private readonly activityLogger: ActivityLogger,
        private readonly unitStorage: UnitStorage,
        route: ActivatedRoute,
        languagesStore: LanguagesStore,
        changeDetectorRef: ChangeDetectorRef,
        ) {

        this.editedUser$ = route.parent.data.pipe(
            filter(x => !!x),
            map(data => data[routeData.user] as User)
        );
        this.languages = languagesStore.allLanguages;
        this.unitOptions = unitStorage.unitOptions;

        this.subscription = combineLatest(
            [this.languages,
            this.editedUser$]
        ).subscribe(([languages, user]) => {
            this.preferredLanguage = languages.find(l => l.languageRowId === user.settings.preferredLanguageId);
            this.preferredUnit = this.unitOptions.find(option => option.id === user.settings.preferredUnits);

            changeDetectorRef.markForCheck();
        });
    }

    public async onLanguageChange(culture: CultureInfo[]) {
        await this.editedUser$.pipe(map(async u => {
            this.activityLogger.logChangePreferredLanguageActivity(u.metadata.userId);
            u.settings.preferredLanguageId  = culture[0].languageRowId;
            await this.saveSettings(u);
        })).toPromise();
    }

    public async onUnitChange(unit: UnitOption[]) {
        await this.editedUser$.pipe(map(async u => {
            this.activityLogger.logChangePreferredUnitsActivity(u.metadata.userId);
            u.settings.preferredUnits = unit[0].id;
            this.unitStorage.setUnit(unit[0]);
            await this.saveSettings(u);
        })).toPromise();
    }

    public ngOnDestroy() {
        this.subscription.unsubscribe();
    }

    private async saveSettings(user: User) {
        await this.userSettingsStorage.save(user.settings, user.metadata.userId);
    }
}
